// src/services/subscriptionService.js
import { doc, getDoc, updateDoc, setDoc, deleteDoc, collection, addDoc, query, where, getDocs, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import adminService from './adminService';
import { PLAN_PERKS } from '../config/plans';

/**
 * Subscription Service
 * Handles subscription status checks and access control
 */
class SubscriptionService {
  /**
   * Check if current user is admin
   * @returns {boolean} True if user is admin
   */
  isAdmin() {
    return adminService.isAdmin();
  }

  /**
   * Get user subscription status
   * @param {string} userId - User ID
   * @param {Object} options - Optional behavior flags
   * @param {boolean} options.skipAdminOverride - If true, do not force admin users to active
   * @param {boolean} options.disableSyncWrite - Deprecated no-op (the client never syncs subscription fields back anymore)
   * @returns {Promise<Object>} Subscription status object
   */
  async getSubscriptionStatus(userId, options = {}) {
    try {
      const { skipAdminOverride = false } = options;

      // ADMIN OVERRIDE: Admins always have premium access (only for their own account)
      if (!skipAdminOverride && this.isAdmin() && userId === auth.currentUser?.uid) {
        console.log('Admin user detected - granting premium access');
        return {
          status: 'active',
          plan: 'admin',
          isActive: true,
          isFree: false,
          startDate: null,
          endDate: null,
          daysRemaining: 999, // Unlimited for admins
          isAdmin: true,
        };
      }

      if (!userId) {
        return this.getDefaultFreeStatus();
      }

      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return this.getDefaultFreeStatus();
      }

      const userData = userDoc.data();
      const now = new Date();
      
      // Check subscription status from user document
      let subscriptionStatus = userData.subscriptionStatus || 'free';
      let subscriptionEndDate = userData.subscriptionEndDate?.toDate();
      let subscriptionStartDate = userData.subscriptionStartDate?.toDate();
      let currentPlan = userData.currentPlan || 'free';

      // IMPORTANT: Also check subscriptions collection for active subscriptions
      // This handles cases where subscription status might be "completed" but still valid
      try {
        const subscriptionsRef = collection(db, 'subscriptions');
        const subsQuery = query(subscriptionsRef, where('userId', '==', userId));
        const subsSnapshot = await getDocs(subsQuery);
        
        if (!subsSnapshot.empty) {
          // Find the most recent subscription that hasn't expired
          const validSubscriptions = subsSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(sub => {
              const rawStatus = String(sub.status || '').toLowerCase();
              // 'expired' and 'inactive' immediately revoke access.
              // 'cancelled'/'canceled' means the user cancelled but access runs until endDate.
              const isImmediatelyRevoked = ['expired', 'inactive'].includes(rawStatus);
              const endDate = sub.endDate?.toDate();
              return endDate && now < endDate && !isImmediatelyRevoked;
            })
            .sort((a, b) => {
              const aDate = a.endDate?.toDate() || new Date(0);
              const bDate = b.endDate?.toDate() || new Date(0);
              return bDate - aDate; // Most recent first
            });
          
          if (validSubscriptions.length > 0) {
            const activeSub = validSubscriptions[0];
            console.log('✅ Found valid subscription in subscriptions collection:', activeSub);
            
            // Use subscription data from subscriptions collection
            subscriptionStatus = 'active'; // Override to active if not expired
            subscriptionEndDate = activeSub.endDate?.toDate();
            subscriptionStartDate = activeSub.startDate?.toDate();
            currentPlan = activeSub.planId || currentPlan;
            
            // NOTE: The client no longer syncs subscription fields back onto the
            // user document. Firestore rules forbid owners from writing premium
            // fields (anti-tampering). The Stripe webhook (Admin SDK) is the
            // authoritative writer; this in-memory override is enough for the UI.
          }
        }
      } catch (subsError) {
        console.warn('⚠️ Error checking subscriptions collection:', subsError);
        // Continue with user document data
      }

      // Check if subscription is still active based on end date.
      // 'cancelled'/'canceled' = user cancelled but access continues until endDate (not immediately revoked).
      // 'expired'/'inactive'/'free' = no access.
      let isActive = false;
      const normalizedStatus = String(subscriptionStatus || '').toLowerCase();
      const isNonActiveStatus = ['free', 'expired', 'inactive'].includes(normalizedStatus);
      const isCancelledStatus = ['cancelled', 'canceled'].includes(normalizedStatus);
      if (subscriptionEndDate && (!isNonActiveStatus || isCancelledStatus)) {
        isActive = now < subscriptionEndDate;
      }

      // If subscription expired, update status
      if (subscriptionStatus === 'active' && !isActive) {
        // Note: In production, you might want to update this in Firestore
        // For now, we'll just return the expired status
        return {
          status: 'expired',
          plan: currentPlan,
          isActive: false,
          isFree: true,
          startDate: subscriptionStartDate,
          endDate: subscriptionEndDate,
          daysRemaining: 0,
        };
      }

      // Calculate days remaining only if subscription is active
      let daysRemaining = 0;
      if (isActive && subscriptionEndDate) {
        const diffTime = subscriptionEndDate - now;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        // Use Math.floor to show full days remaining
        // This gives the number of complete 24-hour periods left
        daysRemaining = Math.max(0, Math.floor(diffDays));
      }

      return {
        status: isActive ? subscriptionStatus : 'free',
        plan: isActive ? currentPlan : 'free',
        isActive,
        isFree: !isActive || subscriptionStatus === 'free',
        startDate: subscriptionStartDate,
        endDate: subscriptionEndDate,
        daysRemaining,
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return this.getDefaultFreeStatus();
    }
  }

  /**
   * Check if user has access to premium features
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} True if user has premium access
   */
  async hasPremiumAccess(userId) {
    // ADMIN OVERRIDE: Admins always have premium access
    if (this.isAdmin()) {
      return true;
    }
    const status = await this.getSubscriptionStatus(userId);
    return status.isActive && !status.isFree;
  }

  /**
   * Check if user can access exam (free users only get first exam)
   * @param {string} userId - User ID
   * @param {string} provider - Exam provider (TELC, Goethe, OSD)
   * @param {string} level - Exam level (B1, B2, C1)
   * @param {number} examIndex - Index of the exam (0 = first exam)
   * @returns {Promise<boolean>} True if user can access this exam
   */
  async canAccessExam(userId, provider, level, examIndex = 0) {
    // ADMIN OVERRIDE: Admins have access to all exams
    if (this.isAdmin()) {
      return true;
    }

    const status = await this.getSubscriptionStatus(userId);

    // Free users can only access the first exam (index 0)
    if (status.isFree) {
      return examIndex === 0;
    }

    // Weekly plan: limited to 3 mock exams (indices 0, 1, 2)
    if (status.isActive && status.plan === 'weekly') {
      return examIndex < 3;
    }

    // Monthly/quarterly premium users have access to all exams
    return status.isActive;
  }

  /**
   * Get default free status
   * @returns {Object} Default free subscription status
   */
  getDefaultFreeStatus() {
    return {
      status: 'free',
      plan: 'free',
      isActive: false,
      isFree: true,
      startDate: null,
      endDate: null,
      daysRemaining: 0,
    };
  }

  /**
   * Initialize free tier subscription for a new user.
   *
   * No longer called from anywhere: createEnhancedUserProfile already writes
   * these defaults at signup, so this only ever ran on users who were past that
   * point — including subscribers re-running onboarding, whose paid plan it
   * overwrote with 'free'. Firestore rules now reject owner writes to these
   * fields, so this can only succeed for an admin.
   *
   * Kept, guarded, because "make sure this account is on the free tier" is a
   * reasonable thing to want; it must simply never be able to downgrade a
   * paying customer.
   *
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Update result
   */
  async initializeFreeTier(userId) {
    try {
      // getSubscriptionStatus consults the subscriptions collection as well as
      // the user doc, so this holds even if the user doc is already corrupted.
      const existing = await this.getSubscriptionStatus(userId, { skipAdminOverride: true });
      if (existing.isActive && !existing.isFree) {
        console.warn(`Refusing to downgrade ${userId}: ${existing.plan} plan active until ${existing.endDate}`);
        return { success: true, skipped: 'has-active-subscription' };
      }

      const userRef = doc(db, 'users', userId);

      await setDoc(userRef, {
        subscriptionStatus: 'free',
        currentPlan: 'free',
        subscriptionStartDate: serverTimestamp(),
        subscriptionEndDate: null,
        lastUpdated: serverTimestamp(),
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.error('Error initializing free tier:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate end date for a plan type
   * @param {string} planType - Plan type ('monthly' or 'quarterly')
   * @returns {Date} End date
   */
  getEndDateForPlan(planType) {
    const now = new Date();
    const endDate = new Date(now);

    if (planType === 'weekly') {
      endDate.setDate(now.getDate() + 7); // 7 days
    } else if (planType === 'monthly') {
      endDate.setDate(now.getDate() + 30); // 30 days
    } else if (planType === 'quarterly') {
      endDate.setDate(now.getDate() + 90); // 90 days
    } else if (planType === 'premium') {
      endDate.setDate(now.getDate() + 180); // 180 days
    } else {
      // Default to monthly
      endDate.setDate(now.getDate() + 30);
    }

    return endDate;
  }

  /**
   * Assign subscription to a user (admin only)
   * @param {string} userId - User ID
   * @param {string} planType - Plan type ('monthly' or 'quarterly')
   * @returns {Promise<Object>} Assignment result
   */
  async assignSubscription(userId, planType) {
    try {
      // Check if current user is admin
      if (!this.isAdmin()) {
        return { success: false, error: 'Admin access required' };
      }

      if (!userId) {
        return { success: false, error: 'User ID is required' };
      }

      if (!planType || !['weekly', 'monthly', 'quarterly', 'premium'].includes(planType)) {
        return { success: false, error: 'Invalid plan type. Must be "weekly", "monthly", "quarterly" or "premium"' };
      }

      const planName = planType === 'weekly' ? '1 week' : planType === 'monthly' ? '1 month' : planType === 'premium' ? 'Premium' : '3 months';
      const endDate = this.getEndDateForPlan(planType);
      
      // Convert Date to Firestore Timestamp
      const endDateTimestamp = Timestamp.fromDate(endDate);
      const startDateTimestamp = Timestamp.now();

      // Update user subscription status
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscriptionStatus: 'active',
        currentPlan: planType,
        subscriptionStartDate: startDateTimestamp,
        subscriptionEndDate: endDateTimestamp,
        lastUpdated: serverTimestamp(),
      });

      // Release the free-tier "one completed exam" flag, exactly as the Stripe
      // webhook does on a real purchase. Without this an admin-granted plan
      // would still be unable to save a second exam result, because
      // firestore.rules blocks the create on flag presence alone. Admin-only
      // delete is permitted by the usage/{flagId} rule.
      try {
        await deleteDoc(doc(db, 'users', userId, 'usage', 'exam'));
      } catch (flagErr) {
        console.warn('Could not clear free exam flag for', userId, flagErr);
      }

      // Create subscription record in subscriptions collection
      const subscriptionData = {
        userId,
        planId: planType,
        planName: planName,
        amount: 0, // Admin-assigned subscriptions are free
        currency: 'eur',
        status: 'active',
        // Same stamp as the Stripe webhook: teacher time included in the plan.
        perks: PLAN_PERKS[planType] ?? { consultations: 0, teacherCorrections: 0 },
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        assignedBy: auth.currentUser?.email || 'admin',
        assignedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        isAdminAssigned: true,
      };

      await addDoc(collection(db, 'subscriptions'), subscriptionData);

      console.log(`✅ Admin assigned ${planName} subscription to user ${userId}`);
      return { success: true, message: `Successfully assigned ${planName} subscription` };
    } catch (error) {
      console.error('Error assigning subscription:', error);
      return { success: false, error: error.message || 'Failed to assign subscription' };
    }
  }

  /**
   * Revoke subscription from a user (admin only)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Revocation result
   */
  async revokeSubscription(userId) {
    try {
      // Check if current user is admin
      if (!this.isAdmin()) {
        return { success: false, error: 'Admin access required' };
      }

      if (!userId) {
        return { success: false, error: 'User ID is required' };
      }

      // Update user subscription status to free
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        subscriptionStatus: 'free',
        currentPlan: 'free',
        subscriptionEndDate: null,
        subscriptionStartDate: null,
        lastUpdated: serverTimestamp(),
      });

      // Also cancel all active subscriptions in the subscriptions collection
      const subscriptionsRef = collection(db, 'subscriptions');
      const subsQuery = query(subscriptionsRef, where('userId', '==', userId));
      const subsSnapshot = await getDocs(subsQuery);
      let cancelledCount = 0;
      let updatedCount = 0;
      const now = new Date();
      
      // Update all subscriptions to cancelled
      for (const subDoc of subsSnapshot.docs) {
        const subData = subDoc.data();
        const updates = {
          cancelledAt: serverTimestamp(),
          cancelledBy: 'admin',
        };

        if (subData.status !== 'cancelled') {
          updates.status = 'cancelled';
          cancelledCount += 1;
        }

        // Force expiration so any legacy endDate-only checks also treat this as inactive.
        const endDate = subData.endDate?.toDate?.();
        if (!endDate || endDate > now) {
          updates.endDate = serverTimestamp();
        }

        await updateDoc(doc(db, 'subscriptions', subDoc.id), updates);
        updatedCount += 1;
        console.log(`✅ Revoked subscription ${subDoc.id} for user ${userId}`);
      }
      
      console.log(`✅ Admin revoked subscription from user ${userId} (${updatedCount} records updated, ${cancelledCount} status changes)`);
      return {
        success: true,
        message: `Successfully revoked subscription (${updatedCount} records updated, ${cancelledCount} set to cancelled)`
      };
    } catch (error) {
      console.error('Error revoking subscription:', error);
      return { success: false, error: error.message || 'Failed to revoke subscription' };
    }
  }
}

export default new SubscriptionService();
